import Divider from "antd/lib/divider";
import Skeleton from "antd/lib/skeleton";
import { get } from "lodash";
import React, { useContext } from "react";
import { useQuery } from "react-query";
import { createApiClient } from "apiClient";
import { AppAvatar } from "components/avatar";
import { AppCard } from "components/card";
import { TopbarExtension } from "components/topbarExtension";
import { UserRoles } from "constants/roles";
import { CommonRoutesRef } from "constants/routes";
import { UserContext, UserContextState } from "contextApis/userContext";
import { startCase } from "helpers/utils";
import { PatientProfileData } from "sharedTypes";
import {
  PageTitleText,
  ProfileHeaderContainer,
  AvatarContainer,
  AvatarTextContainer,
  AvatarNameText,
  AvatarSpecialityText,
  ResetPasswordLinkContainer,
  ResetPasswordLink,
  ProfileContentContainer,
  GridItem,
  GridItemTitle,
  GridItemValue,
  GridItemGap,
  ContactNumberContainer,
  ContactNumberGap
} from "./style";

export const MyProfilePatientPage: React.FC = () => {
  const { userData, onLogout } = useContext<UserContextState>(UserContext);
  const onResetPasswordClick = () => {
    onLogout(`/${CommonRoutesRef.ForgotPassword}`);
  };

  const userId = sessionStorage.getItem("userId");

  const { data, isFetching, isLoading } = useQuery<PatientProfileData>(
    "patient-profile",
    async () => {
      return await createApiClient().getPatientProfile(
        userId ? userId : "",
        true
      );
    }
  );

  const phoneNumberArray = data?.phoneNumbers || userData?.phoneNumbers || [];

  const userIsPatient =
    sessionStorage.getItem("userRole") === UserRoles.PATIENT;

  const showTopbarExtesion = !userIsPatient;

  return (
    <>
      {showTopbarExtesion && (
        <TopbarExtension>
          <PageTitleText>My Profile</PageTitleText>
        </TopbarExtension>
      )}
      <AppCard cardWidth="100%" cardHeight="auto">
        <ProfileHeaderContainer>
          <AvatarContainer>
            <AppAvatar imageSrc={get(userData, "picture[0]", "")} size={80} />
            <AvatarTextContainer>
              {isLoading || isFetching ? (
                <Skeleton
                  loading={isFetching || isLoading}
                  active={isFetching || isLoading}
                  avatar={false}
                  paragraph={false}
                  style={{ width: 100 }}
                />
              ) : (
                <AvatarNameText>{`${get(userData, "lastName", "")}, ${get(
                  userData,
                  "firstName",
                  ""
                )}`}</AvatarNameText>
              )}
              {isLoading || isFetching ? (
                <Skeleton
                  loading={isFetching || isLoading}
                  active={isFetching || isLoading}
                  avatar={false}
                  paragraph={false}
                  style={{ width: 30 }}
                />
              ) : (
                <AvatarSpecialityText>
                  {get(userData, "degree", "")}
                </AvatarSpecialityText>
              )}
            </AvatarTextContainer>
          </AvatarContainer>
          <ResetPasswordLinkContainer>
            <ResetPasswordLink onClick={onResetPasswordClick}>
              Reset Password
            </ResetPasswordLink>
          </ResetPasswordLinkContainer>
        </ProfileHeaderContainer>
        <Divider />
        <ProfileContentContainer>
          <GridItem>
            <GridItemTitle topPadding>Date of Birth</GridItemTitle>
            {isLoading || isFetching ? (
              <Skeleton
                loading={isFetching || isLoading}
                active={isFetching || isLoading}
                avatar={false}
                paragraph={false}
                style={{ width: 100 }}
              />
            ) : (
              <GridItemValue bold>{startCase(data?.dob || "")}</GridItemValue>
            )}
            <GridItemGap />
            <GridItemTitle>Email Address</GridItemTitle>
            {isLoading || isFetching ? (
              <Skeleton
                loading={isFetching || isLoading}
                active={isFetching || isLoading}
                avatar={false}
                paragraph={false}
                style={{ width: 100 }}
              />
            ) : (
              <GridItemValue bold>{data?.email}</GridItemValue>
            )}
            <GridItemGap />
            <GridItemTitle>Role</GridItemTitle>
            {isLoading || isFetching ? (
              <Skeleton
                loading={isFetching || isLoading}
                active={isFetching || isLoading}
                avatar={false}
                paragraph={false}
                style={{ width: 100 }}
              />
            ) : (
              <GridItemValue bold>{startCase(data?.role || "")}</GridItemValue>
            )}
            <GridItemGap />
            <GridItemTitle>Gender</GridItemTitle>
            {isLoading || isFetching ? (
              <Skeleton
                loading={isFetching || isLoading}
                active={isFetching || isLoading}
                avatar={false}
                paragraph={false}
                style={{ width: 100 }}
              />
            ) : (
              <GridItemValue bold>
                {data?.gender
                  ? data.gender.charAt(0).toUpperCase() + data.gender.slice(1)
                  : ""}
              </GridItemValue>
            )}
          </GridItem>
          <GridItem leftBorder>
            <GridItemTitle topPadding>Home Address</GridItemTitle>
            {isFetching || isLoading ? (
              <Skeleton
                loading={isFetching || isLoading}
                active={isFetching || isLoading}
                avatar={false}
                paragraph={false}
                style={{ width: 150 }}
              />
            ) : (
              <>
                <GridItemValue bold>
                  {get(data, "home_addr_1", "")}
                </GridItemValue>
                <GridItemValue bold>
                  {get(data, "home_addr_2", "")}
                </GridItemValue>
                <GridItemValue bold>{`${get(data, "address_city", "")}${
                  data ? "," : ""
                } ${get(data, "state", "") || ""} ${get(
                  data,
                  "address_zip",
                  ""
                )}`}</GridItemValue>
              </>
            )}
            <GridItemGap />
            <GridItemTitle>Contact Number</GridItemTitle>
            <ContactNumberContainer>
              {isLoading || isFetching ? (
                <Skeleton
                  loading={isFetching || isLoading}
                  active={isFetching || isLoading}
                  avatar={false}
                  paragraph={{ rows: 1 }}
                  style={{ width: 100 }}
                />
              ) : (
                phoneNumberArray.map((phoneNumber, index) => (
                  <div key={index}>
                    <GridItemTitle>{phoneNumber.title}</GridItemTitle>
                    <GridItemValue>{phoneNumber.number}</GridItemValue>
                    {index < phoneNumberArray.length - 1 && (
                      <ContactNumberGap />
                    )}
                  </div>
                ))
              )}
            </ContactNumberContainer>
          </GridItem>
        </ProfileContentContainer>
      </AppCard>
    </>
  );
};
